import { useEffect } from "react";

const DIALOG_SELECTOR = '[role="dialog"], [role="alertdialog"], .modal-box, .card.modal, .analytics-modal';
const DISMISS_SELECTOR = [
  "[data-dialog-dismiss]",
  ".modal-close",
  ".modal-close-btn",
  'button[aria-label="Close"]',
  'button[aria-label="Close dialog"]',
].join(",");

const isVisible = (element: HTMLElement): boolean => {
  const style = window.getComputedStyle(element);
  return !element.hidden && element.getAttribute("aria-hidden") !== "true" && style.display !== "none" && style.visibility !== "hidden";
};

/**
 * Ensures every rendered application dialog can be dismissed with Escape.
 * Existing dialog-specific handlers remain authoritative; this only activates
 * when the topmost dialog is still open after they have handled the key.
 */
export function useGlobalDialogEscape() {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      const dialogs = Array.from(document.querySelectorAll<HTMLElement>(DIALOG_SELECTOR)).filter(isVisible);
      const topmostDialog = dialogs.at(-1);
      if (!topmostDialog) return;

      queueMicrotask(() => {
        if (event.defaultPrevented || !topmostDialog.isConnected || !isVisible(topmostDialog)) return;
        const explicitDismiss = topmostDialog.querySelector<HTMLButtonElement>(DISMISS_SELECTOR);
        const dismiss = explicitDismiss || Array.from(topmostDialog.querySelectorAll<HTMLButtonElement>("button"))
          .find((button) => ["cancel", "close", "stay here"].includes(button.textContent?.trim().toLowerCase() || ""));
        if (!dismiss || dismiss.disabled) return;
        dismiss.click();
      });
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);
}
